import { cadenzaUrl } from '@disy/lib.backend/cadenza-url';
import { createAbortError } from '@disy/lib/abort-error';
import { logger } from '@disy/lib/logging';
import { MIMETYPE_JSON } from '@disy/lib/media-type';
import type { Opaque } from '@disy/lib/opaque';
import { delay } from '@disy/lib/promise-utils';

import { ajax, createEventSource } from 'cadenza/api-client/api';

/**
 * Result id for endpoints whose work order produces a
 * net.disy.cadenza.workorder.web.dto.FileDownloadWorkOrderResult.
 * Can be passed into {@link getWorkOrderResultUrl} to get the download url for the file produced by
 * the work order.
 */
export const FILE_DOWNLOAD_RESULT_ID = 'file';

const MAX_PROGRESS_RECONNECT_COUNT = 5;

export type WorkOrderId = Opaque<'WorkOrderId', string>;

interface ObservingWorkOrderPromise<StartedEvent, ProgressEvent, Result> extends Promise<Result> {
  started: (listener: (event: StartedEvent) => void) => ObservingWorkOrderPromise<StartedEvent, ProgressEvent, Result>;
  progress: (
    listener: (event: ProgressEvent) => void,
  ) => ObservingWorkOrderPromise<StartedEvent, ProgressEvent, Result>;
}

/** @deprecated Use {@link #startWorkOrder} with an <code>AbortSignal</code> instead. */
export class WorkOrderCancelController {
  readonly #workOrderId: WorkOrderId;
  #canceled: boolean;

  constructor(workOrderId: WorkOrderId) {
    this.#workOrderId = workOrderId;
    this.#canceled = false;
  }

  cancel() {
    if (!this.#canceled) {
      deleteWorkOrders([this.#workOrderId])
        .then(() => {
          this.#canceled = true;
        })
        .catch(() => Error("WorkOrder can't be deleted."));
    }
  }
}

function createWorkOrderAbortedError() {
  return createAbortError('WorkOrder canceled');
}

export type WorkOrderError = Error & { key?: number | string; context?: unknown };

/**
 * Checks if the parameter is a WorkOrderError
 *
 * @param error - object to check if it's a WorkOrderError, usually the given parameter
 * from a .catch promise block or a try-catch block
 * @return true, if WorkOrderError, else false
 */
export function isWorkOrderError(error: unknown): error is WorkOrderError {
  if (error && typeof error === 'object') {
    const workOrderError = error as { key: unknown; context: unknown };
    return (
      (typeof workOrderError.key === 'string' ||
        typeof workOrderError.key === 'number' ||
        typeof workOrderError.key === 'undefined') &&
      (typeof workOrderError.context === 'string' || typeof workOrderError.context === 'undefined')
    );
  }
  return false;
}

export function getWorkOrderResultUrl(workOrderId: WorkOrderId, resultId: string, inline?: boolean): string {
  let url = `/workOrder/${workOrderId}/results/${resultId}`;
  if (inline) {
    url += '?inline';
  }
  return cadenzaUrl(url);
}

/**
 * Deletes the workOrder specified by the given id. If the workOrder is still running, it's cancelled.
 *
 * @param workOrderIds
 * @return - Promise that is resolved when the workOrder is cancelled.
 */
export function deleteWorkOrders(workOrderIds: WorkOrderId[]): Promise<void> {
  return ajax(`/workOrder;workOrderIds=${workOrderIds.join()}`, { type: 'DELETE' });
}

/** @deprecated Use the more convenient {@link #startWorkOrder} */
export function observeForResult<StartedEvent, ProgressEvent, Result>(
  workOrderId: string,
  resultEventName: string,
  customEventListeners?: { [key: string]: (eventData: MessageEvent) => void },
): ObservingWorkOrderPromise<StartedEvent, ProgressEvent, Result> {
  if (!workOrderId) {
    throw new Error(`Must supply a workOrderId to observe for result '${resultEventName}'`);
  }
  // The endpoint supports observing multiple workOrders at once. For simplicity we just observe one
  // here for now as the promise, listeners and users would else need to handle multiple calls.
  const eventSource = createEventSource(`/workOrder;workOrderIds=${workOrderId}/monitor`);
  const startedListeners: ((event: StartedEvent) => void)[] = [];
  const progressListeners: ((event: ProgressEvent) => void)[] = [];
  const promise = new Promise((resolve, reject) => {
    let reconnectTryCount = 0;
    let resultReceived = false;
    let isCanceled = false;

    for (const [customEventName, customEventListener] of Object.entries(customEventListeners ?? {})) {
      logger.debug(`Registering event listener for custom SSE type: ${customEventName}.`);
      eventSource.addEventListener(customEventName, customEventListener);
    }

    eventSource.addEventListener('workorder-started', (event) => {
      const startedData = JSON.parse(event.data);
      startedListeners.forEach((startedListener) => startedListener(startedData));
    });

    eventSource.addEventListener('workorder-progress', (event) => {
      reconnectTryCount = 0;
      const progressData = JSON.parse(event.data);
      progressListeners.forEach((progressListener) => progressListener(progressData));
    });

    eventSource.addEventListener(resultEventName, (event) => {
      resultReceived = true;
      resolve(JSON.parse(event.data));
    });

    eventSource.addEventListener('workorder-canceled', () => {
      resultReceived = false;
      isCanceled = true;
      eventSource.close();
      reject(createWorkOrderAbortedError());
    });

    eventSource.addEventListener('workorder-stopped', (event) => {
      eventSource.close();
      const eventData = JSON.parse(event.data);
      const errorCode = eventData.errorCode;
      if (errorCode) {
        const error: WorkOrderError = new Error();
        error.key = errorCode;
        if (eventData.value) {
          error.context = eventData.value;
        }
        reject(error);
      }
      if (!resultReceived) {
        reject(new Error('WorkOrder stopped without receiving a result.'));
      }
    });

    eventSource.onerror = function () {
      if (eventSource.readyState === EventSource.CLOSED) {
        if (isCanceled) {
          reject(createWorkOrderAbortedError());
        } else {
          reject(new Error('WorkOrder progress request failed'));
        }
      } else if (reconnectTryCount >= MAX_PROGRESS_RECONNECT_COUNT) {
        eventSource.close();
        reject(new Error('WorkOrder progress request max reconnect count reached'));
      } else {
        logger.warn('Trying to reconnect to a WorkOrder progress SSE source');
        reconnectTryCount++;
      }
    };
  });

  const workOrderPromise = Object.assign(promise, {
    started(startedListener: (event: StartedEvent) => void) {
      startedListeners.push(startedListener);
      return workOrderPromise;
    },
    progress(progressListener: (event: ProgressEvent) => void) {
      progressListeners.push(progressListener);
      return workOrderPromise;
    },
  }) as ObservingWorkOrderPromise<StartedEvent, ProgressEvent, Result>;
  return workOrderPromise;
}

/**
 * Starts a work order.
 *
 * _Notes:_
 *
 * - The result `Promise` is rejected with an `AbortError` if the work order was cancelled (using the `signal` option
 *   or in the backend). So be sure to use [isAbortError(error)]{@link import('@disy/lib/abort-error').isAbortError}
 *   in your error handling, typically to ignore such errors.
 * - The result is automatically delayed (using [delay(promise)]{@link import('@disy/lib/promise-utils').delay}),
 *   because by definition work orders should take some time.
 *
 * @param url - The URL of the endpoint to request for starting the work order
 * @param resultEventName - The name of the work order's result event
 * @param [options]
 * @param [options.data] - A request payload, which is JSON-stringified.
 * @param [options.signal] - A signal to abort the work order (also in the backend)
 * @param [options.onStart] - A callback for the start event of the work order
 * @param [options.onProgress] - A callback for progress events of the work order
 * @param [options.customEventListeners] - Listeners for custom work order events
 * @return The result of the work order
 */
export async function startWorkOrder<Result = never, ProgressEvent = never, StartedEvent = never>(
  url: string,
  resultEventName: string,
  {
    data,
    signal,
    onStart,
    onProgress,
    customEventListeners,
  }: {
    data?: unknown;
    signal?: AbortSignal;
    onStart?: (event: StartedEvent) => void;
    onProgress?: (event: ProgressEvent) => void;
    customEventListeners?: Record<string, (event: MessageEvent) => void>;
  } = {},
) {
  const workOrderId = await ajax<WorkOrderId>(url, {
    method: 'POST',
    data: data !== undefined ? JSON.stringify(data) : data,
    contentType: MIMETYPE_JSON,
    signal,
  });
  const promise = observeForResult<StartedEvent, ProgressEvent, Result>(
    workOrderId,
    resultEventName,
    customEventListeners,
  );
  if (signal) {
    signal.addEventListener('abort', () => deleteWorkOrders([workOrderId]));
  }
  if (onStart) {
    promise.started(onStart);
  }
  if (onProgress) {
    promise.progress(onProgress);
  }
  return delay(promise, () => signal?.aborted ?? false);
}
